import React from 'react';
import { useTranslation } from 'react-i18next';
import { AbsenceResultChip } from '@/components/AbsenceApplication/AbsenceResultChip';
import { useAbsenceCancellationState } from '@/components/AbsenceApplication/hooks/useAbsenceCancellationState';
import type { NonAttendanceApplicationDetail } from '@/components/AbsenceApplication/types';
import { Alert } from '@/components/Icons/Alert';
import { CheckCircleFilled } from '@/components/Icons/CheckCircleFilled';
import { Retry } from '@/components/Icons/Retry';
import { AbsenceResultChipKind } from '@/types/components';
import { formatDateTime } from '@/utils/date';

const { Info, Warning, Error: ChipError } = AbsenceResultChipKind;

interface Props {
  application: NonAttendanceApplicationDetail;
  isRetrying: boolean;
  hideRetryButton?: boolean;
  onRetry: () => void;
}

export const AbsenceCancellationCard: React.FC<Props> = ({
  application,
  isRetrying,
  hideRetryButton = false,
  onRetry,
}) => {
  const { t } = useTranslation();
  const {
    absenceLabel,
    cateringPreservedLabel,
    cateringCanceledLabel,
    isRiga,
    showCateringPreserved,
    showCateringCanceledChip,
    showCateringError,
    showRetry,
    showSentTo,
    periodsMatch,
    cancellationAuthor,
  } = useAbsenceCancellationState(application);
  const canceledAt = formatDateTime(cancellationAuthor.timeModified);

  return (
    <div className="flex w-full flex-col gap-4 rounded-xl bg-card p-4 shadow-card">
      <p className="text-callout-16 text-foreground">
        {t('absenceApplication.detail.cancelationTitle')}
      </p>

      <div className="flex flex-col items-start gap-2">
        <div className="flex w-full items-center gap-1.5">
          <CheckCircleFilled className="size-4 shrink-0" />
          <p className="text-body-14 text-muted-foreground">
            {t('absenceApplication.detail.absenceCanceled')}
          </p>
        </div>
        <AbsenceResultChip kind={Info} label={absenceLabel} />
      </div>

      {showCateringPreserved && (
        <div className="flex flex-col items-start gap-2">
          <div className="flex w-full items-center gap-1.5">
            <CheckCircleFilled className="size-4 shrink-0" />
            <p className="text-body-14 text-muted-foreground">
              {t('absenceApplication.result.cateringNeeded')}
            </p>
          </div>
          <AbsenceResultChip kind={Info} label={cateringPreservedLabel} />
        </div>
      )}

      {showCateringCanceledChip && (
        <div className="flex flex-col items-start gap-2">
          <div className="flex w-full items-center gap-1.5">
            <CheckCircleFilled className="size-4 shrink-0" />
            <p className="text-body-14 text-muted-foreground">
              {t('absenceApplication.detail.cateringRefusalCanceled')}
            </p>
          </div>
          <AbsenceResultChip
            kind={periodsMatch ? Info : Warning}
            label={cateringCanceledLabel}
          />
        </div>
      )}

      {showCateringError && (
        <div className="flex flex-col items-start gap-2">
          <div className="flex w-full items-start gap-1.5">
            <Alert className="mt-[2px] size-4 shrink-0 text-destructive" />
            <p className="text-body-14 text-muted-foreground">
              {t('absenceApplication.detail.cateringRefusalCanceled')}
            </p>
          </div>
          <AbsenceResultChip
            kind={ChipError}
            label={t('absenceApplication.result.cateringGeneralError')}
          />
        </div>
      )}

      <div className="flex flex-col gap-0.5">
        {showSentTo && isRiga && (
          <p className="text-caption-12 self-end text-subtle-foreground">
            {t('absenceApplication.result.sentToRiga')}
          </p>
        )}
        {showSentTo && !isRiga && (
          <p className="text-caption-12 self-end text-subtle-foreground">
            {t('absenceApplication.result.sentToMily')}
          </p>
        )}
        <p className="text-caption-12 self-end text-subtle-foreground">
          {t('absenceApplication.detail.canceledBy', {
            name: cancellationAuthor.authorName,
            datetime: canceledAt,
          })}
        </p>
      </div>

      {!hideRetryButton && showRetry && (
        <>
          <div className="h-px w-full bg-border" />
          <button
            type="button"
            onClick={onRetry}
            disabled={isRetrying}
            className="text-subhead-14 flex cursor-pointer items-center justify-center gap-2 font-medium text-primary disabled:cursor-not-allowed"
          >
            <Retry className={`size-4 ${isRetrying ? 'animate-spin' : ''}`} />
            {t('absenceApplication.result.retry')}
          </button>
        </>
      )}
    </div>
  );
};
